import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  IconButton,
  Grid,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NoteIcon from '@mui/icons-material/Note';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { printReceipt as sendPrintReceipt } from '../../../../services/localPrintService';
import { getPackageOrderById } from '../../../../services/salesService';
import { getirApi, migrosApi, yemeksepetiApi, trendyolApi, mainApi } from '../../../../config/apiClient';
import { printPackageReceipt, PackageReceiptData } from '../components/PackageReceiptPrintService';
import { getYsDeliveryLabel } from '../utils/yemeksepetiDelivery';
import { resolveReceiptHeader, resolveReceiptLabel, buildReceiptOrderRef } from '../utils/receiptHeader';
import { ORDER_SOURCE_TO_PLATFORM } from '../utils/helpers';
import type { PlatformIntegration } from '../../../../types/platformIntegration';

type OrderPlatform = 'phone' | 'getir' | 'migros' | 'yemeksepeti' | 'trendyol' | 'kiosk' | 'qr' | 'ecommerce';

interface ScreenData {
  localServiceUrl?: string;
  printingSettings?: {
    defaultReceiptPrinter?: { id: string; name: string } | null;
    receiptDisplaySettings?: {
      companyName?: string;
      companyAddress?: string;
      taxNumber?: string;
      headerType?: 'text' | 'logo';
      logoUrl?: string;
      logoMaxWidthPx?: number;
      logoMaxHeightPx?: number;
      standardFooterNote?: string;
      thankYouMessage?: string;
    };
  };
  /** Marka bazlı fiş başlığını çözebilmek için entegrasyon listesi. */
  platformIntegrations?: PlatformIntegration[];
}

export interface UnifiedOrderDetailModalProps {
  open: boolean;
  onClose: () => void;
  orderId: number | string | null;
  platform: OrderPlatform | null;
  companyId: string;
  screenData?: ScreenData | null;
}

interface NormalizedOrder {
  id: string | number;
  platform: OrderPlatform;
  orderNumber: string;
  shortCode?: string;
  customerName: string;
  customerPhone?: string;
  customerAddress?: string;
  status: string;
  statusLabel: string;
  statusColor: 'success' | 'warning' | 'info' | 'secondary' | 'default' | 'error';
  createdAt: string;
  totalAmount: number;
  subtotal?: number;
  discountAmount?: number;
  paymentMethod?: string;
  isOnlinePaid?: boolean;
  customerNote?: string;
  courierName?: string;
  courierPhone?: string;
  deliveryType?: string;
  items: NormalizedItem[];
  rawData?: any;
  trendyolOrderCode?: string;
  callCenterPhone?: string;
  isPreOrder?: boolean;
  expectedDeliveryTime?: string;
  // PR1 cancel akışı (telefon siparişleri için).
  // Backend `PackageOrderDto` zaten `CancelReason`, `CancelNote`, `CancelledAt`
  // alanlarını döndürür; modal iptal durumunda kullanıcıya gösterir.
  cancelReason?: string;
  cancelNote?: string;
  cancelledAt?: string;
}

interface NormalizedItem {
  name: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  note?: string;
  subItems?: { name: string }[];
}

const PLATFORM_CONFIG: Record<OrderPlatform, { icon: React.ReactElement; color: string; label: string }> = {
  phone: { icon: <PhoneIcon sx={{ fontSize: 20 }} />, color: '#4CAF50', label: 'Telefon Siparisi' },
  getir: { icon: <ShoppingCartIcon sx={{ fontSize: 20 }} />, color: '#5D3EBC', label: 'Getir Yemek' },
  migros: { icon: <StorefrontIcon sx={{ fontSize: 20 }} />, color: '#FF6000', label: 'Migros Yemek' },
  yemeksepeti: { icon: <RestaurantMenuIcon sx={{ fontSize: 20 }} />, color: '#FA0050', label: 'Yemeksepeti' },
  trendyol: { icon: <TwoWheelerIcon sx={{ fontSize: 20 }} />, color: '#06C167', label: 'Uber Eats' },
  kiosk: { icon: <PointOfSaleIcon sx={{ fontSize: 20 }} />, color: '#607D8B', label: 'Kiosk Siparisi' },
  qr: { icon: <QrCode2Icon sx={{ fontSize: 20 }} />, color: '#2196F3', label: 'QR Menu Siparisi' },
  ecommerce: { icon: <ShoppingBagIcon sx={{ fontSize: 20 }} />, color: '#9C27B0', label: 'E-Ticaret Siparisi' },
};

const normalizeStatus = (platform: OrderPlatform, status?: string | null): { label: string; color: 'success' | 'warning' | 'info' | 'secondary' | 'default' | 'error' } => {
  if (!status) return { label: 'Bilinmiyor', color: 'default' };
  const s = status.toString().toLowerCase();

  if (platform === 'getir') {
    if (['350', '400'].includes(status)) return { label: 'Yeni', color: 'success' };
    if (['500'].includes(status)) return { label: 'Onaylandi', color: 'info' };
    if (['550'].includes(status)) return { label: 'Hazirlaniyor', color: 'warning' };
    if (['600', '700', '800'].includes(status)) return { label: 'Yolda', color: 'secondary' };
    if (['900'].includes(status)) return { label: 'Teslim Edildi', color: 'default' };
    if (['1500', '1600'].includes(status)) return { label: 'Iptal', color: 'error' };
  }

  if (['new', 'pending', 'created', 'bekliyor', 'yeni'].includes(s)) return { label: 'Yeni', color: 'success' };
  if (['accepted', 'approved', 'onaylandi', 'picking'].includes(s)) return { label: 'Onaylandi', color: 'info' };
  if (['preparing', 'hazirlaniyor', 'invoiced'].includes(s)) return { label: 'Hazirlaniyor', color: 'warning' };
  if (['ready', 'hazir', 'on_delivery', 'on_way', 'onway', 'shipped', 'picked_up'].includes(s)) return { label: 'Yolda', color: 'secondary' };
  if (['delivered', 'completed', 'teslim edildi', 'tamamlandi'].includes(s)) return { label: 'Teslim Edildi', color: 'default' };
  if (['cancelled', 'rejected', 'expired', 'iptal'].includes(s)) return { label: 'Iptal', color: 'error' };
  return { label: status, color: 'default' };
};

const formatDateTime = (dateStr: string): { date: string; time: string } => {
  if (!dateStr) return { date: '-', time: '-' };
  try {
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString('tr-TR', { timeZone: 'Europe/Istanbul' }),
      time: date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Istanbul' }),
    };
  } catch {
    return { date: '-', time: '-' };
  }
};

/** Trendyol adres JSON'unu okunabilir metne cevirir */
const formatTrendyolAddress = (addr: string | null | undefined): string | undefined => {
  if (!addr || typeof addr !== 'string') return undefined;
  const trimmed = addr.trim();
  if (!trimmed) return undefined;
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed) as Record<string, string>;
      const parts: string[] = [];
      const a1 = parsed.Address1 ?? parsed.address1 ?? '';
      if (a1) parts.push(a1);
      const a2 = parsed.Address2 ?? parsed.address2 ?? '';
      if (a2) parts.push(a2);
      const nb = parsed.Neighborhood ?? parsed.neighborhood ?? '';
      if (nb) parts.push(nb);
      const dist = parsed.District ?? parsed.district ?? '';
      if (dist) parts.push(dist);
      const city = parsed.City ?? parsed.city ?? '';
      if (city) parts.push(city);
      const extras: string[] = [];
      const apt = parsed.ApartmentNumber ?? parsed.apartmentNumber ?? '';
      if (apt) extras.push(`Bina: ${apt}`);
      const floor = parsed.Floor ?? parsed.floor ?? '';
      if (floor) extras.push(`Kat: ${floor}`);
      const door = parsed.DoorNumber ?? parsed.doorNumber ?? '';
      if (door) extras.push(`Daire: ${door}`);
      const desc = parsed.AddressDescription ?? parsed.addressDescription ?? '';
      if (desc) extras.push(desc);
      if (extras.length) parts.push(extras.join(', '));
      return parts.length ? parts.join(', ') : undefined;
    } catch {
      return addr;
    }
  }
  return addr;
};

// Platform-specific data normalizers
const normalizePackageOrder = (data: any, platform: OrderPlatform): NormalizedOrder => {
  const st = normalizeStatus(platform, data.OrderStatus || data.orderStatus);
  return {
    id: data.Id ?? data.id,
    platform,
    orderNumber: data.OrderNumber ?? data.orderNumber ?? '',
    shortCode: data.ShortCode ?? data.shortCode,
    customerName: data.AccountName ?? data.accountName ?? '-',
    customerPhone: data.CustomerPhone ?? data.customerPhone,
    customerAddress: data.CustomerAddress ?? data.customerAddress,
    status: data.OrderStatus ?? data.orderStatus ?? '',
    statusLabel: st.label,
    statusColor: st.color,
    createdAt: data.CreatedAt ?? data.createdAt ?? '',
    totalAmount: data.TotalAmount ?? data.totalAmount ?? 0,
    subtotal: data.SubTotal ?? data.subTotal,
    paymentMethod: data.PaymentMethod ?? data.paymentMethod,
    isOnlinePaid: ((data.PaymentMethod ?? data.paymentMethod) || '').toLowerCase().includes('online'),
    customerNote: data.CustomerNotes ?? data.customerNotes,
    courierName: data.CourierName ?? data.courierName,
    courierPhone: data.CourierPhone ?? data.courierPhone,
    items: (data.Items ?? data.items ?? []).map((it: any) => ({
      name: it.ProductName ?? it.productName ?? 'Urun',
      quantity: it.Quantity ?? it.quantity ?? 1,
      unitPrice: it.UnitPrice ?? it.unitPrice ?? 0,
      totalPrice: it.TotalAmount ?? it.totalAmount ?? (it.UnitPrice ?? it.unitPrice ?? 0) * (it.Quantity ?? it.quantity ?? 1),
      note: it.Notes ?? it.notes,
    })),
    // PR1: Telefon siparişi iptal akışı — DTO zaten bu alanları döndürür.
    cancelReason: data.CancelReason ?? data.cancelReason ?? undefined,
    cancelNote: data.CancelNote ?? data.cancelNote ?? undefined,
    cancelledAt: data.CancelledAt ?? data.cancelledAt ?? undefined,
    rawData: data,
  };
};

const normalizeGetirOrder = (data: any): NormalizedOrder => {
  const st = normalizeStatus('getir', String(data.status));
  const isOnline = data.paymentMethod === 1;
  return {
    id: data.id,
    platform: 'getir',
    orderNumber: data.confirmationId ?? data.getirId?.substring(0, 8) ?? String(data.id),
    customerName: data.clientName ?? '-',
    customerPhone: data.clientPhone,
    customerAddress: [data.deliveryAddress, data.aptNo ? `Bina: ${data.aptNo}` : '', data.floor ? `Kat: ${data.floor}` : '', data.doorNo ? `Daire: ${data.doorNo}` : ''].filter(Boolean).join(', '),
    status: String(data.status),
    statusLabel: st.label,
    statusColor: st.color,
    createdAt: data.checkoutDate ?? data.createdAt ?? '',
    totalAmount: (data.totalDiscountedPrice != null && data.totalDiscountedPrice > 0 && data.totalDiscountedPrice !== data.totalPrice)
      ? data.totalDiscountedPrice : (data.totalPrice ?? 0),
    subtotal: data.totalPrice,
    discountAmount: (data.totalDiscountedPrice != null && data.totalDiscountedPrice > 0 && data.totalDiscountedPrice !== data.totalPrice)
      ? data.totalPrice - data.totalDiscountedPrice : undefined,
    paymentMethod: data.paymentMethodText || (isOnline ? 'Online Odeme' : 'Kapida Odeme'),
    isOnlinePaid: isOnline,
    customerNote: [data.clientNote, data.doNotKnock ? 'Kapiya vurma' : null, data.dropOffAtDoor ? 'Kapiya birak' : null].filter(Boolean).join(' | ') || undefined,
    courierName: data.courierName,
    deliveryType: data.deliveryType === 1 ? 'Getir Getirsin' : 'Restoran Getirsin',
    isPreOrder: data.isScheduled || false,
    expectedDeliveryTime: data.scheduledDate || undefined,
    items: (data.items ?? []).map((it: any) => ({
      name: it.name ?? 'Urun',
      quantity: it.quantity ?? 1,
      unitPrice: it.price ?? 0,
      totalPrice: it.totalPriceWithOption ?? it.totalPrice ?? 0,
      note: it.note,
      subItems: it.options?.map((opt: any) => ({ name: `+ ${opt.optionName}${opt.price > 0 ? ` (+${opt.price.toFixed(2)})` : ''}` })) || [],
    })),
    rawData: data,
  };
};

const normalizeMigrosOrder = (data: any): NormalizedOrder => {
  const st = normalizeStatus('migros', data.status);
  const totalTL = (data.discountedPrice || data.totalPrice || 0) / 100;
  const originalTL = (data.totalPrice || 0) / 100;
  return {
    id: data.id,
    platform: 'migros',
    orderNumber: data.shortCode || String(data.migrosOrderId || data.id),
    customerName: data.customerFullName ?? '-',
    customerPhone: data.customerPhoneNumber,
    customerAddress: [data.addressDetail, data.districtName, data.townName, data.cityName].filter(Boolean).join(', '),
    status: data.status ?? '',
    statusLabel: st.label,
    statusColor: st.color,
    createdAt: data.createdAt ?? '',
    totalAmount: totalTL,
    subtotal: originalTL,
    discountAmount: originalTL > totalTL ? originalTL - totalTL : undefined,
    paymentMethod: data.isOnlinePayment ? 'Online Odeme' : (data.paymentType || 'Kapida Odeme'),
    isOnlinePaid: data.isOnlinePayment,
    customerNote: [data.orderNote, data.contactlessDelivery ? 'Temassiz Teslimat' : null, data.ringDoorBell === false ? 'Kapi zilini calma' : null, data.saveGreen ? 'Cevreci Paketleme' : null].filter(Boolean).join(' | ') || undefined,
    courierName: data.assignedCourierName,
    deliveryType: data.deliveryProvider === 'MIGROS' ? 'Migros Kurye' : 'Restoran Kuryesi',
    items: (data.items ?? []).map((it: any) => {
      const subItems: { name: string }[] = [];
      if (it.options && Array.isArray(it.options)) {
        it.options.forEach((opt: any) => {
          subItems.push({ name: `+ ${opt.headerName || ''}: ${opt.itemNames || opt.name || ''}` });
        });
      }
      return {
        name: it.name ?? 'Urun',
        quantity: it.amount ?? 1,
        unitPrice: (it.unitPrice || it.price || 0) / 100,
        totalPrice: (it.price || 0) / 100,
        subItems: subItems.length > 0 ? subItems : undefined,
      };
    }),
    rawData: data,
  };
};

const normalizeYsOrder = (data: any): NormalizedOrder => {
  const st = normalizeStatus('yemeksepeti', data.status);
  const ptUpper = (data.paymentType || '').toUpperCase().replace(/\u0130/g, 'I').replace(/\u015E/g, 'S');
  const isDoorPay = ptUpper.includes('KAPIDA') || ptUpper.includes('DOOR') || ptUpper.includes('ON_DELIVERY');
  const isOnline = !isDoorPay && (ptUpper.includes('ONLINE') || ptUpper.includes('PAY_WITH_CARD') || ptUpper.startsWith('PAY_WITH'));
  return {
    id: data.id,
    platform: 'yemeksepeti',
    orderNumber: data.shortCode || data.code || String(data.id),
    customerName: data.customerFullName ?? '-',
    customerPhone: data.customerPhone,
    customerAddress: [data.deliveryAddress, data.deliveryDistrict, data.deliveryCity].filter(Boolean).join(', '),
    status: data.status ?? '',
    statusLabel: st.label,
    statusColor: st.color,
    createdAt: data.createdAt ?? '',
    totalAmount: data.grandTotal ?? 0,
    paymentMethod: isOnline ? 'Online Odeme' : (data.paymentType || 'Kapida Odeme'),
    isOnlinePaid: isOnline,
    customerNote: data.customerComment,
    courierName: data.assignedCourierName,
    deliveryType: getYsDeliveryLabel(data),
    items: (data.items ?? []).map((it: any) => {
      const subItems: { name: string }[] = [];
      if (it.variationName && it.variationName !== it.name) subItems.push({ name: it.variationName });
      try {
        const toppings = it.toppingsJson ? JSON.parse(it.toppingsJson) : [];
        toppings.forEach((t: any) => {
          const price = parseFloat(t.price ?? '0');
          subItems.push({ name: `+ ${t.name}${price > 0 ? ` (+${price.toFixed(2)})` : ''}` });
        });
      } catch { /* ignore */ }
      return {
        name: it.name ?? 'Urun',
        quantity: it.quantity ?? 1,
        unitPrice: it.unitPrice ?? 0,
        totalPrice: it.paidPrice ?? it.unitPrice ?? 0,
        subItems: subItems.length > 0 ? subItems : undefined,
      };
    }),
    rawData: data,
    isPreOrder: data.isPreOrder ?? false,
    expectedDeliveryTime: data.expectedDeliveryTime,
  };
};

const normalizeTrendyolOrder = (data: any): NormalizedOrder => {
  const st = normalizeStatus('trendyol', data.orderStatus ?? data.OrderStatus ?? data.packageStatus);
  const payMethod = (data.paymentMethod ?? data.PaymentMethod ?? '').toLowerCase();
  const isOnline = data.isPaid ?? data.IsPaid ?? (payMethod !== 'pay_with_on_delivery' && payMethod !== '');

  const rawDetails = data.rawOrderDetails ?? data.RawOrderDetails;
  const rawLines = rawDetails?.lines ?? rawDetails?.orderLines ?? [];

  const dbLines: any[] = data.orderLines ?? data.OrderLines ?? [];

  let items: NormalizedItem[];

  if (rawLines.length > 0) {
    items = rawLines.map((line: any) => {
      const subItems: { name: string }[] = [];
      const processModifiers = (mods: any[], depth = 0) => {
        if (!mods) return;
        mods.forEach((mod: any) => {
          subItems.push({ name: `${'  '.repeat(depth)}+ ${mod.name}${mod.price > 0 ? ` (+${Number(mod.price).toFixed(2)})` : ''}` });
          if (mod.modifierProducts?.length) processModifiers(mod.modifierProducts, depth + 1);
        });
      };
      processModifiers(line.modifierProducts ?? line.ModifierProducts);
      (line.extraIngredients ?? line.ExtraIngredients ?? []).forEach((e: any) => subItems.push({ name: `+ ${e.name}` }));
      (line.removedIngredients ?? line.RemovedIngredients ?? []).forEach((r: any) => subItems.push({ name: `- ${r.name} (cikarildi)` }));
      const qty = line.items?.filter((i: any) => !i.isCancelled).length || 1;
      return {
        name: line.name ?? line.Name ?? 'Urun',
        quantity: qty,
        unitPrice: line.unitSellingPrice ?? line.UnitSellingPrice ?? line.price ?? line.Price ?? 0,
        totalPrice: (line.unitSellingPrice ?? line.price ?? 0) * qty,
        subItems: subItems.length > 0 ? subItems : undefined,
      };
    });
  } else if (dbLines.length > 0) {
    items = dbLines.map((line: any) => {
      const subItems: { name: string }[] = [];
      const modifiers: any[] = line.modifiers ?? line.Modifiers ?? [];
      modifiers.forEach((mod: any) => {
        const modName = mod.modifierName ?? mod.ModifierName ?? mod.name;
        const modPrice = Number(mod.price ?? mod.Price ?? 0);
        subItems.push({ name: `+ ${modName}${modPrice > 0 ? ` (+${modPrice.toFixed(2)})` : ''}` });
      });
      let selectedOpts: any[] = [];
      try {
        const optJson = line.selectedOptionsJson ?? line.SelectedOptionsJson;
        if (optJson) selectedOpts = typeof optJson === 'string' ? JSON.parse(optJson) : optJson;
      } catch { /* ignore */ }
      selectedOpts.forEach((opt: any) => subItems.push({ name: `+ ${opt.name ?? opt.Name}` }));
      return {
        name: line.productName ?? line.ProductName ?? line.name ?? 'Urun',
        quantity: line.quantity ?? line.Quantity ?? 1,
        unitPrice: Number(line.unitPrice ?? line.UnitPrice ?? 0),
        totalPrice: Number(line.totalPrice ?? line.TotalPrice ?? 0),
        note: line.notes ?? line.Notes,
        subItems: subItems.length > 0 ? subItems : undefined,
      };
    });
  } else {
    const fallbackLines = data.lines ?? [];
    items = fallbackLines.map((line: any) => {
      const subItems: { name: string }[] = [];
      const processModifiers = (mods: any[], depth = 0) => {
        if (!mods) return;
        mods.forEach((mod: any) => {
          subItems.push({ name: `${'  '.repeat(depth)}+ ${mod.name}${mod.price > 0 ? ` (+${Number(mod.price).toFixed(2)})` : ''}` });
          if (mod.modifierProducts?.length) processModifiers(mod.modifierProducts, depth + 1);
        });
      };
      processModifiers(line.modifierProducts ?? line.ModifierProducts);
      const qty = line.items?.filter((i: any) => !i.isCancelled).length || 1;
      return {
        name: line.name ?? line.Name ?? 'Urun',
        quantity: qty,
        unitPrice: line.unitSellingPrice ?? line.UnitSellingPrice ?? line.price ?? 0,
        totalPrice: (line.unitSellingPrice ?? line.price ?? 0) * qty,
        subItems: subItems.length > 0 ? subItems : undefined,
      };
    });
  }

  return {
    id: data.id ?? data.Id,
    platform: 'trendyol',
    orderNumber: data.orderNumber ?? data.OrderNumber ?? data.orderCode ?? data.OrderCode ?? '',
    shortCode: data.orderCode ?? data.OrderCode,
    customerName: data.customerName ?? data.CustomerName ?? (data.customer ? `${data.customer.firstName} ${data.customer.lastName}` : '-'),
    customerPhone: data.customerPhone ?? data.CustomerPhone ?? data.address?.phone,
    customerAddress: formatTrendyolAddress(data.deliveryAddress ?? data.DeliveryAddress) ?? (data.address ? [data.address.address1, data.address.neighborhood, data.address.district, data.address.city].filter(Boolean).join(', ') : undefined),
    status: data.orderStatus ?? data.OrderStatus ?? data.packageStatus ?? '',
    statusLabel: st.label,
    statusColor: st.color,
    createdAt: data.createdAt ?? data.CreatedAt ?? (data.orderDate ?? data.OrderDate ? new Date(data.orderDate ?? data.OrderDate).toISOString() : '') ?? (data.packageCreationDate ? new Date(data.packageCreationDate).toISOString() : ''),
    totalAmount: Number(data.totalAmount ?? data.TotalAmount ?? data.totalPrice ?? 0),
    discountAmount: data.discountAmount != null ? Number(data.discountAmount) : undefined,
    paymentMethod: isOnline ? 'Online Odeme' : 'Kapida Odeme',
    isOnlinePaid: !!isOnline,
    customerNote: data.orderNote ?? data.OrderNote ?? data.customerNote,
    courierName: data.assignedCourierName ?? data.AssignedCourierName,
    deliveryType: (data.deliveryType ?? data.DeliveryType) === 'GO' ? 'Gel Al' : 'Teslimat',
    trendyolOrderCode: data.orderNumber ?? data.OrderNumber ?? data.orderCode ?? data.OrderCode,
    callCenterPhone: data.callCenterPhone ?? data.CallCenterPhone,
    items,
    rawData: data,
  };
};

const normalizeMasaSiparisi = (data: any, platform: OrderPlatform): NormalizedOrder => {
  const st = normalizeStatus(platform, data.siparisDurumu ?? data.SiparisDurumu);
  let items: NormalizedItem[] = [];
  try {
    const detayStr = data.siparisDetaylari ?? data.SiparisDetaylari;
    if (detayStr) {
      const parsed = typeof detayStr === 'string' ? JSON.parse(detayStr) : detayStr;
      const arr = parsed.siparisDetaylari || parsed;
      if (Array.isArray(arr)) {
        items = arr.map((it: any) => ({
          name: it.urunAdi ?? it.name ?? 'Urun',
          quantity: it.adet ?? it.miktar ?? it.quantity ?? 1,
          unitPrice: it.birimFiyat ?? it.unitPrice ?? 0,
          totalPrice: it.toplamFiyat ?? it.totalPrice ?? 0,
          note: it.notlar ?? it.aciklama,
        }));
      }
    }
  } catch { /* ignore */ }

  let siparisNotu: string | undefined;
  try {
    const detayStr = data.siparisDetaylari ?? data.SiparisDetaylari;
    if (typeof detayStr === 'string') {
      const parsed = JSON.parse(detayStr);
      siparisNotu = parsed.siparisNotu;
    }
  } catch { /* ignore */ }

  return {
    id: data.id ?? data.Id,
    platform,
    orderNumber: data.siparisId ?? data.SiparisId ?? '',
    customerName: data.kullaniciAdi ?? data.KullaniciAdi ?? data.musteriAdi ?? '-',
    customerPhone: data.musteriTelefon,
    customerAddress: data.musteriAdres,
    status: data.siparisDurumu ?? data.SiparisDurumu ?? '',
    statusLabel: st.label,
    statusColor: st.color,
    createdAt: data.siparisOlusturmaTarihi ?? data.SiparisOlusturmaTarihi ?? '',
    totalAmount: data.toplamTutar ?? data.ToplamTutar ?? 0,
    paymentMethod: data.odemeYontemi ?? data.OdemeYontemi ?? (data.odemeDurumu === 'Odendi' ? 'Online' : undefined),
    isOnlinePaid: data.odemeBasarili === true,
    customerNote: siparisNotu ?? data.siparisNotu ?? data.SiparisNotu,
    items,
    rawData: data,
  };
};

const UnifiedOrderDetailModal: React.FC<UnifiedOrderDetailModalProps> = ({
  open, onClose, orderId, platform, companyId, screenData,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [order, setOrder] = useState<NormalizedOrder | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);

  const config = platform ? PLATFORM_CONFIG[platform] : null;

  const isGetir = platform === 'getir';
  const isMigros = platform === 'migros';
  const isYemeksepeti = platform === 'yemeksepeti';
  const isTrendyol = platform === 'trendyol';
  const isPackageOrder = platform === 'phone';
  const isMasaSiparisi = platform === 'qr' || platform === 'kiosk' || platform === 'ecommerce';

  const loadOrderDetails = useCallback(async () => {
    if (!orderId || !platform || !companyId) return;
    setLoading(true);
    setError(null);

    try {
      let normalizedData: NormalizedOrder | null = null;

      if (isPackageOrder) {
        const response = await getPackageOrderById(Number(orderId));
        if (response.Success && response.Data) {
          normalizedData = normalizePackageOrder(response.Data, 'phone');
        } else {
          throw new Error(response.Message || 'Siparis yuklenemedi');
        }
      } else if (isGetir) {
        const res = await getirApi.get(`/api/getir-orders/order/${orderId}`);
        const result = res.data;
        const data = result.data || result.Data || result;
        normalizedData = normalizeGetirOrder(data);
      } else if (isMigros) {
        const res = await migrosApi.get(`/api/migros-orders/order/${orderId}`);
        const result = res.data;
        const data = result.data || result.Data || result;
        normalizedData = normalizeMigrosOrder(data);
      } else if (isYemeksepeti) {
        const res = await yemeksepetiApi.get(`/api/ys-orders/order/${orderId}`);
        const result = res.data;
        const data = result.data || result.Data || result;
        normalizedData = normalizeYsOrder(data);
      } else if (isTrendyol) {
        const res = await trendyolApi.get(`/api/trendyol/order/${orderId}`);
        const result = res.data;
        const data = result.data || result.Data || result;
        normalizedData = normalizeTrendyolOrder(data);
      } else if (isMasaSiparisi) {
        const res = await mainApi.get(`/api/MasaSiparisi/${orderId}`);
        const result = res.data;
        const data = result.data || result.Data || result;
        normalizedData = normalizeMasaSiparisi(data, platform);
      }

      if (normalizedData) {
        setOrder(normalizedData);
      } else {
        throw new Error('Siparis verisi bulunamadi');
      }
    } catch (err: any) {
      console.error('[UnifiedOrderDetail] Hata:', err);
      setError(err.message || 'Siparis detaylari yuklenirken hata olustu');
    } finally {
      setLoading(false);
    }
  }, [orderId, platform, companyId, isPackageOrder, isGetir, isMigros, isYemeksepeti, isTrendyol, isMasaSiparisi]);

  useEffect(() => {
    if (open && orderId && platform) {
      loadOrderDetails();
    } else {
      setOrder(null);
      setError(null);
    }
  }, [open, orderId, platform, loadOrderDetails]);

  const handlePrintReceipt = useCallback(async () => {
    if (!order) return;

    const printer = screenData?.printingSettings?.defaultReceiptPrinter;
    if (!printer) {
      alert('Fis yazicisi tanimlanmamis. Lutfen ekran ayarlarindan yazici secin.');
      return;
    }

    setIsPrinting(true);
    try {
      // Markaya özel fiş başlığı; marka tanımlı değilse ekran varsayılanı.
      // QR/kiosk/telefon siparişlerinde platform eşleşmesi olmadığı için
      // çözümleyici doğrudan ekran varsayılanını döndürür.
      const headerOrderRef = buildReceiptOrderRef(
        ORDER_SOURCE_TO_PLATFORM[order.platform] || '',
        null,
        order.rawData,
      );
      const brandHeader = resolveReceiptHeader(headerOrderRef, screenData);
      const localServiceUrl = screenData?.localServiceUrl || 'http://localhost:3003';
      const dt = formatDateTime(order.createdAt);

      if (isGetir || isMigros) {
        const receiptData: any = {
          items: order.items.map(it => ({
            name: it.name,
            quantity: it.quantity,
            unitPrice: it.unitPrice,
            totalPrice: it.totalPrice,
            subItems: it.subItems,
          })),
          orderNumber: order.orderNumber,
          customerName: order.customerName,
          customerPhone: order.customerPhone || '',
          deliveryAddress: order.customerAddress || '',
          invoiceNumber: order.orderNumber,
          date: dt.date,
          time: dt.time,
          grandTotal: order.totalAmount,
          subtotal: order.subtotal ?? order.totalAmount,
          discountTotal: order.discountAmount ?? 0,
          paymentMethod: order.paymentMethod,
          isOnlinePaid: order.isOnlinePaid,
          receiptLabel: resolveReceiptLabel(
            isGetir ? 'GETIR PAKET SIPARIS' : 'MIGROS PAKET SIPARIS',
            headerOrderRef,
            screenData,
          ),
          deliveryType: order.deliveryType,
          customerNote: order.customerNote,
          ...brandHeader,
          logoMaxWidthPx: brandHeader.logoMaxWidthPx ?? 384,
          logoMaxHeightPx: brandHeader.logoMaxHeightPx ?? 150,
          showReceiptHeader: true,
        };

        await sendPrintReceipt({
          printerId: printer.id,
          printerName: printer.name,
          receiptData,
        }, { baseUrl: localServiceUrl });
      } else {
        const receiptData: PackageReceiptData = {
          orderNumber: order.orderNumber,
          shortCode: order.shortCode,
          date: dt.date,
          time: dt.time,
          customerName: order.customerName,
          customerPhone: order.customerPhone,
          customerAddress: order.customerAddress,
          items: order.items.map(it => ({
            name: it.name,
            quantity: it.quantity,
            unitPrice: it.unitPrice,
            totalPrice: it.totalPrice,
            subItems: it.subItems,
          })),
          subtotal: order.subtotal ?? order.totalAmount,
          vatTotal: 0,
          grandTotal: order.totalAmount,
          paymentStatus: order.isOnlinePaid ? 'PAID' : undefined,
          paymentMethod: order.paymentMethod,
          isOnlinePaid: order.isOnlinePaid,
          orderNote: order.customerNote,
          ...brandHeader,
          logoMaxWidthPx: brandHeader.logoMaxWidthPx ?? 384,
          logoMaxHeightPx: brandHeader.logoMaxHeightPx ?? 150,
          trendyolOrderCode: order.trendyolOrderCode,
          callCenterPhone: order.callCenterPhone,
          // Uber Eats siparislerinde fisin orta bolumu Uber'in kendi duzeninde
          // basilir. Bu normalize edilmis akista supplierId tasinmadigi icin
          // destek kodu gonderilmez; yerel servis o parcayi atlar.
          isUberEatsReceipt: isTrendyol || undefined,
          isPreOrder: order.isPreOrder,
          expectedDeliveryTime: order.expectedDeliveryTime,
        };

        await printPackageReceipt(receiptData, printer, localServiceUrl);
      }

      console.log('[UnifiedOrderDetail] Fis yazdirildi');
    } catch (err: any) {
      console.error('[UnifiedOrderDetail] Fis yazdirma hatasi:', err);
      alert(`Fis yazdirma hatasi: ${err.message || 'Bilinmeyen hata'}`);
    } finally {
      setIsPrinting(false);
    }
  }, [order, screenData, isGetir, isMigros]);

  if (!platform || !config) return null;

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6}>
            <CircularProgress size={48} />
            <Typography variant="body1" color="text.secondary" mt={2}>
              Siparis detaylari yukleniyor...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Siparis Detayi</Typography>
            <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" action={
            <Button color="inherit" size="small" onClick={loadOrderDetails} startIcon={<RefreshIcon />}>Tekrar Dene</Button>
          }>{error}</Alert>
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Kapat</Button></DialogActions>
      </Dialog>
    );
  }

  if (!order) return null;

  const dt = formatDateTime(order.createdAt);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderTop: `4px solid ${config.color}` } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar sx={{ bgcolor: config.color, width: 40, height: 40 }}>
              {config.icon}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Siparis #{order.shortCode || order.orderNumber || order.id}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {config.label}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip label={order.statusLabel} color={order.statusColor} size="small" />
            {order.isOnlinePaid && (
              <Chip label="Online Odendi" color="success" size="small" variant="outlined" />
            )}
            <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {order.isPreOrder && order.expectedDeliveryTime && (
          <Box sx={{
            bgcolor: '#ff6f00', color: 'white', p: 2, mb: 2, borderRadius: 2,
            display: 'flex', alignItems: 'center', gap: 2,
            border: '2px solid #e65100',
          }}>
            <Box sx={{ fontSize: 40 }}>⏰</Box>
            <Box>
              <Typography variant="h6" fontWeight="bold">İLERİ TARİHLİ SİPARİŞ</Typography>
              <Typography variant="h5" fontWeight="bold">
                Teslim: {(() => {
                  const utcStr = order.expectedDeliveryTime!;
                  const utcDate = utcStr.endsWith('Z') ? new Date(utcStr) : new Date(utcStr + 'Z');
                  const trMs = utcDate.getTime() + 3 * 60 * 60 * 1000;
                  const trDate = new Date(trMs);
                  const h = String(trDate.getUTCHours()).padStart(2, '0');
                  const m = String(trDate.getUTCMinutes()).padStart(2, '0');
                  const d = String(trDate.getUTCDate()).padStart(2, '0');
                  const mo = String(trDate.getUTCMonth() + 1).padStart(2, '0');
                  return `${d}.${mo} ${h}:${m}`;
                })()}
              </Typography>
            </Box>
          </Box>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            {/* PR1: Telefon siparişi iptal bilgisi — yalnızca iptal edilmiş
                telefon siparişlerinde ve `cancelReason` mevcutken gösterilir.
                Müşteri bilgilerinin üstünde dikkat çeken kırmızı kart. */}
            {order.platform === 'phone' &&
             (order.status?.toString().toUpperCase() === 'CANCELLED' || order.statusLabel === 'Iptal') &&
             (order.cancelReason || order.cancelNote) && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 2,
                  bgcolor: '#FFEBEE',
                  borderColor: '#E53935',
                  borderWidth: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#C62828' }}
                >
                  <CancelOutlinedIcon fontSize="small" /> Iptal Bilgisi
                </Typography>
                <Divider sx={{ my: 1, borderColor: '#EF9A9A' }} />
                <Box display="flex" flexDirection="column" gap={0.5}>
                  {order.cancelReason && (
                    <Typography variant="body2">
                      <strong>Sebep:</strong> {order.cancelReason}
                    </Typography>
                  )}
                  {order.cancelNote && (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      <strong>Aciklama:</strong> {order.cancelNote}
                    </Typography>
                  )}
                  {order.cancelledAt && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Iptal Zamani:</strong> {(() => {
                        const d = formatDateTime(order.cancelledAt!);
                        return `${d.date} ${d.time}`;
                      })()}
                    </Typography>
                  )}
                </Box>
              </Paper>
            )}

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonIcon fontSize="small" /> Musteri Bilgileri
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="body2"><strong>Ad Soyad:</strong> {order.customerName}</Typography>
                {order.customerPhone && (
                  <Typography variant="body2"><strong>Telefon:</strong> {order.customerPhone}</Typography>
                )}
              </Box>
            </Paper>

            {order.customerAddress && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOnIcon fontSize="small" /> Teslimat Adresi
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" color="text.secondary">{order.customerAddress}</Typography>
              </Paper>
            )}

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTimeIcon fontSize="small" /> Siparis Bilgileri
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="body2"><strong>Tarih:</strong> {dt.date}</Typography>
                <Typography variant="body2"><strong>Saat:</strong> {dt.time}</Typography>
                <Typography variant="body2"><strong>Odeme:</strong> {order.paymentMethod || 'Kapida Odeme'}</Typography>
                {order.deliveryType && (
                  <Typography variant="body2"><strong>Teslimat:</strong> {order.deliveryType}</Typography>
                )}
              </Box>
            </Paper>

            {order.courierName && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Kurye Bilgisi</Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2"><strong>Kurye:</strong> {order.courierName}</Typography>
                {order.courierPhone && (
                  <Typography variant="body2"><strong>Telefon:</strong> {order.courierPhone}</Typography>
                )}
              </Paper>
            )}

            {order.customerNote && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'warning.50' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <NoteIcon fontSize="small" /> Musteri Notu
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" fontStyle="italic">{order.customerNote}</Typography>
              </Paper>
            )}

            {isTrendyol && order.trendyolOrderCode && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#E6F7EF', border: '2px solid #06C167' }}>
                <Typography variant="subtitle2" fontWeight="bold" textAlign="center" gutterBottom>
                  KURYE ILETISIM KODU
                </Typography>
                <Typography variant="h4" fontWeight="bold" textAlign="center" letterSpacing={3} color="#06C167">
                  {order.trendyolOrderCode}
                </Typography>
                {order.callCenterPhone && (
                  <Typography variant="caption" display="block" textAlign="center" mt={1}>
                    {order.callCenterPhone} numarasini arayip bu kodu tuslayin
                  </Typography>
                )}
              </Paper>
            )}
          </Grid>

          {/* Right Column - Items */}
          <Grid item xs={12} md={7}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Siparis Detayi ({order.items.length} Urun)
              </Typography>
              <Divider sx={{ my: 1 }} />

              {order.items.length === 0 ? (
                <Box py={3} textAlign="center">
                  <Typography variant="body2" color="text.secondary">Urun bilgisi bulunamadi</Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Urun</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', width: 50 }}>Adet</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', width: 80 }}>B.Fiyat</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', width: 80 }}>Toplam</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {order.items.map((item, index) => (
                        <React.Fragment key={index}>
                          <TableRow>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">{item.name}</Typography>
                              {item.note && (
                                <Typography variant="caption" color="warning.main" display="block">Not: {item.note}</Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">{item.quantity}</TableCell>
                            <TableCell align="right">{item.unitPrice.toFixed(2)} TL</TableCell>
                            <TableCell align="right">{item.totalPrice.toFixed(2)} TL</TableCell>
                          </TableRow>
                          {item.subItems?.map((sub, si) => (
                            <TableRow key={`sub-${index}-${si}`}>
                              <TableCell colSpan={4} sx={{ pl: 4, py: 0.3, borderBottom: 'none' }}>
                                <Typography variant="caption" color="text.secondary">{sub.name}</Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Divider sx={{ my: 2 }} />

              <Box display="flex" flexDirection="column" gap={0.5} alignItems="flex-end">
                {order.discountAmount != null && order.discountAmount > 0 && (
                  <>
                    <Typography variant="body2">Ara Toplam: {(order.subtotal ?? order.totalAmount).toFixed(2)} TL</Typography>
                    <Typography variant="body2" color="success.main">Indirim: -{order.discountAmount.toFixed(2)} TL</Typography>
                  </>
                )}
                <Typography variant="h6" fontWeight="bold" color={config.color}>
                  Genel Toplam: {order.totalAmount.toFixed(2)} TL
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button startIcon={<RefreshIcon />} onClick={loadOrderDetails}>Yenile</Button>
        <Button
          startIcon={<PrintIcon />}
          variant="outlined"
          onClick={handlePrintReceipt}
          disabled={isPrinting || !screenData?.printingSettings?.defaultReceiptPrinter}
        >
          {isPrinting ? 'Yazdiriliyor...' : 'Fis Yazdir'}
        </Button>
        <Button variant="contained" onClick={onClose} sx={{ bgcolor: config.color, '&:hover': { bgcolor: config.color, filter: 'brightness(0.9)' } }}>
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnifiedOrderDetailModal;
